import React, { useState } from 'react';
import { CodeTabs } from '../code/CodeTabs';
import { ApiEndpoint as ApiEndpointType } from '../../types/docs.types';
import {
  ChevronDown,
  ChevronRight,
  AlertCircle,
  CheckCircle,
  Info
} from 'lucide-react';

interface ApiEndpointProps {
  endpoint: ApiEndpointType;
}

export const ApiEndpoint: React.FC<ApiEndpointProps> = ({ endpoint }) => {
  const [expanded, setExpanded] = useState(false);
  const [activeResponseTab, setActiveResponseTab] = useState(0);

  const getMethodColor = (method: string) => {
    switch (method) {
      case 'GET':    return 'bg-blue-50 text-blue-700 border border-blue-200';
      case 'POST':   return 'bg-emerald-50 text-emerald-700 border border-emerald-200';
      case 'PUT':    return 'bg-amber-50 text-amber-700 border border-amber-200';
      case 'DELETE': return 'bg-red-50 text-red-700 border border-red-200';
      case 'PATCH':  return 'bg-purple-50 text-purple-700 border border-purple-200';
      default:       return 'bg-gray-50 text-gray-700 border border-gray-200';
    }
  };

  const getStatusColor = (status: number) => {
    if (status >= 200 && status < 300) return 'text-emerald-600';
    if (status >= 400 && status < 500) return 'text-amber-600';
    if (status >= 500) return 'text-red-600';
    return 'text-gray-600';
  };

  const getStatusBorderColor = (status: number) => {
    if (status >= 200 && status < 300) return 'border-emerald-100';
    if (status >= 400 && status < 500) return 'border-amber-100';
    if (status >= 500) return 'border-red-100';
    return 'border-gray-100';
  };

  const getStatusIcon = (status: number) => {
    if (status >= 200 && status < 300) return <CheckCircle className="h-4 w-4" />;
    if (status >= 400) return <AlertCircle className="h-4 w-4" />;
    return <Info className="h-4 w-4" />;
  };

  return (
    <div
      className="bg-white border border-gray-200 rounded-2xl shadow-sm hover:shadow-md transition-all duration-200 mb-6"
      id={endpoint.id}
    >
      {/* Header */}
      <div className="p-5 sm:p-6">
        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
          <div className="flex-1 min-w-0">
            <div className="flex flex-wrap items-center gap-2 mb-3">
              <span className={`inline-flex items-center px-2.5 py-0.5 rounded-md text-xs font-bold tracking-wide ${getMethodColor(endpoint.method)}`}>
                {endpoint.method}
              </span>
              <code className="text-xs font-mono text-gray-500 bg-gray-100 px-3 py-1.5 rounded-lg border border-gray-200 break-all">
                {endpoint.url}
              </code>
            </div>
            <h3 className="text-lg sm:text-xl font-semibold text-gray-900 mb-1.5">
              {endpoint.title}
            </h3>
            <p className="text-sm text-gray-500 leading-relaxed">{endpoint.description}</p>
          </div>
          <button
            onClick={() => setExpanded(!expanded)}
            className="inline-flex items-center justify-center gap-2 px-4 py-2 bg-[#581689] text-white text-sm font-medium rounded-lg hover:bg-[#4a1275] transition-colors duration-150 shrink-0"
          >
            {expanded
              ? <><ChevronDown className="h-4 w-4" /> Collapse</>
              : <><ChevronRight className="h-4 w-4" /> Expand</>
            }
          </button>
        </div>
      </div>

      {/* Expanded content */}
      {expanded && (
        <div className="border-t border-gray-100 bg-gray-50 rounded-b-2xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 p-5 sm:p-6">

            {/* Left — Documentation */}
            <div className="space-y-7">

              {/* Parameters */}
              {endpoint.parameters && endpoint.parameters.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Parameters</h4>
                  <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                      <table className="min-w-full divide-y divide-gray-100">
                        <thead>
                          <tr className="bg-gray-50">
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Name</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Required</th>
                            <th className="px-4 py-2.5 text-left text-xs font-semibold text-gray-500 uppercase tracking-wide">Description</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {endpoint.parameters.map((param, index) => (
                            <tr key={index} className="hover:bg-gray-50 transition-colors">
                              <td className="px-4 py-3 text-xs font-mono font-semibold text-[#581689]">{param.name}</td>
                              <td className="px-4 py-3 text-xs text-gray-500">{param.type}</td>
                              <td className="px-4 py-3">
                                <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                                  param.required
                                    ? 'bg-red-50 text-red-600 border border-red-100'
                                    : 'bg-gray-100 text-gray-500 border border-gray-200'
                                }`}>
                                  {param.required ? 'Required' : 'Optional'}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-xs text-gray-600">{param.description}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              )}

              {/* Request Body */}
              {endpoint.requestBody && (
                <div>
                  <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Request Body</h4>
                  <p className="text-sm text-gray-500 mb-3 leading-relaxed">{endpoint.requestBody.description}</p>
                  <CodeTabs examples={[{
                    language: 'json',
                    code: JSON.stringify(endpoint.requestBody.example, null, 2),
                    title: 'Example'
                  }]} />
                </div>
              )}

              {/* Responses */}
              <div>
                <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Responses</h4>
                <div className="space-y-2">
                  {endpoint.responses.map((response, index) => (
                    <div key={index} className={`bg-white border rounded-xl overflow-hidden ${getStatusBorderColor(response.status)}`}>
                      <button
                        onClick={() => setActiveResponseTab(activeResponseTab === index ? -1 : index)}
                        className="w-full flex items-center justify-between px-4 py-3 text-left hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <span className={getStatusColor(response.status)}>
                            {getStatusIcon(response.status)}
                          </span>
                          <span className={`font-bold text-sm ${getStatusColor(response.status)}`}>
                            {response.status}
                          </span>
                          <span className="text-sm text-gray-600">{response.description}</span>
                        </div>
                        <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${
                          activeResponseTab === index ? 'rotate-180' : ''
                        }`} />
                      </button>
                      {activeResponseTab === index && response.example !== null && (
                        <div className="border-t border-gray-100 p-4">
                          <CodeTabs examples={[{
                            language: 'json',
                            code: JSON.stringify(response.example, null, 2),
                            title: 'Response'
                          }]} />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Right — Code Examples */}
            <div>
              <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Code Examples</h4>
              <div className="sticky top-6">
                <CodeTabs examples={endpoint.codeExamples} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
